// We want these to match the API schema as closely as possible

import type { JSONSchema7 } from 'json-schema';

import type { DeepReadonly } from './types';

export const deepSeekJsonSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    frequency_penalty: {
      type: ['number', 'null'],
      description:
        'Penalizes new tokens based on their existing frequency in the text, decreasing repetition likelihood.',
      minimum: -2.0,
      maximum: 2.0,
      default: 0,
    },
    max_tokens: {
      type: ['integer', 'null'],
      description: 'The maximum number of tokens that can be generated in the chat completion.',
      minimum: 1,
      maximum: 8192,
      default: 4096,
    },
    presence_penalty: {
      type: ['number', 'null'],
      description:
        'Penalizes new tokens based on whether they appear in the text, increasing likelihood of new topics.',
      minimum: -2.0,
      maximum: 2.0,
      default: 0,
    },
    response_format: {
      type: ['object', 'null'],
      description: 'Defines the format of the response.',
    },
    stop: {
      type: ['object', 'null'],
      description: 'Stop condition configuration.',
    },
    stream: {
      type: ['boolean', 'null'],
      description: 'If true, enables streaming responses where tokens are sent as they become available.',
    },
    stream_options: {
      type: ['object', 'null'],
      description: 'Additional configuration options for streaming.',
    },
    temperature: {
      type: ['number', 'null'],
      description: 'Sampling temperature for randomness in response generation.',
      minimum: 0,
      maximum: 2,
      default: 1,
    },
    top_p: {
      type: ['number', 'null'],
      description: 'Nucleus sampling probability threshold, limiting considered tokens.',
      minimum: 0,
      maximum: 1,
      default: 1,
    },
    tools: {
      type: ['array', 'null'],
      description: 'A list of tool objects available for use.',
      items: {
        type: 'object',
      },
    },
    tool_choice: {
      type: ['object', 'null'],
      description: 'Specifies the tool selection strategy.',
    },
    logprobs: {
      type: ['boolean', 'null'],
      description: 'If true, returns log probabilities of output tokens.',
    },
    top_logprobs: {
      type: ['integer', 'null'],
      description: 'Number of top likely tokens to return per token position.',
      minimum: 0,
      maximum: 20,
    },
  },
  additionalProperties: false,
} as const;

// this confirms that the type is a valid JSON schema, but we want it as a `const` so we can use it to index the properties
export const deepSeekSchemaProperties: DeepReadonly<JSONSchema7> = deepSeekJsonSchema;

export const retrieveDeepSeekParameters = (): string[] => Object.keys(deepSeekJsonSchema.properties);
